import React, { useState, useEffect, useCallback } from 'react';
import pdsService from '../services/pdsService';
import sharepointService from '../services/sharepointService';
import clientHelperService from '../services/clientHelperService';
import { buildPdsPdfBlob, defaultPdsPdfFilename } from './pdsPdfExport';

/**
 * Publish a Personal Data Sheet PDF to SharePoint and the knowledgebase.
 *
 * Mirrors the IM and FPR publish flows, with one deliberate difference: a PDS
 * lands in the client's MAIN folder, not in Client Services Docs or Financial
 * Planning. There is therefore no subfolder to find or create — the
 * destination is always the client folder root.
 */
export default function PdsSharePointModal({ client, onClose, onSuccess }) {
  const defaultFilename = defaultPdsPdfFilename(client.name);

  const [spClients, setSpClients] = useState([]);
  const [spClient, setSpClient] = useState('');
  const [newSpClientName, setNewSpClientName] = useState('');

  const [kbClients, setKbClients] = useState([]);
  const [kbClientId, setKbClientId] = useState('');
  const [kbClientName, setKbClientName] = useState('');
  const [newKbClientName, setNewKbClientName] = useState('');

  const [folderPath, setFolderPath] = useState('');
  const [loadingFolders, setLoadingFolders] = useState(false);

  const [filename, setFilename] = useState(defaultFilename);
  const [overwrite, setOverwrite] = useState(false);

  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [error, setError] = useState('');

  /** Resolve the client's main folder — the "/" entry the API returns. */
  const loadFolders = useCallback(async (clientName) => {
    if (!clientName) return '';
    setLoadingFolders(true);
    setFolderPath('');

    try {
      const result = await sharepointService.getClientFolders(clientName);
      const folderList = result.folders || [];
      const rootEntry = folderList.find((f) => f.name === '/');
      const resolvedPath = rootEntry?.full_path || clientName;
      setFolderPath(resolvedPath);
      return resolvedPath;
    } catch (loadErr) {
      console.error('Failed to load folders:', loadErr);
      return '';
    } finally {
      setLoadingFolders(false);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clients, spFolders] = await Promise.all([
          clientHelperService.fetchClients().catch(() => []),
          sharepointService.getClients().catch(() => []),
        ]);
        setKbClients(clients);
        setSpClients(spFolders);

        const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z]/g, '');
        const rawName = (client.name || '').trim();
        const parts = rawName.includes(',')
          ? rawName.split(',').map((s) => s.trim())
          : rawName.split(/\s+/).reverse();
        const last = parts[0] || '';
        const first = parts[1] || '';
        const nLast = normalize(last);
        const nFirst = normalize(first);

        const scoreCandidate = (candidate) => {
          const n = normalize(candidate);
          if (!n) return 0;
          const exactJoin = nLast + nFirst;
          const exactReverse = nFirst + nLast;
          if (n === exactJoin || n === exactReverse) return 100;
          if (nLast && nFirst && n.startsWith(nLast) && n.slice(nLast.length).startsWith(nFirst.slice(0, 3))) return 90;
          if (nLast && nFirst && n.startsWith(nFirst) && n.slice(nFirst.length).startsWith(nLast.slice(0, 3))) return 85;
          if (nLast && n.includes(nLast) && nLast.length >= 3) return 60;
          return 0;
        };

        let matchedKbId = '';
        let matchedKbName = '';

        if (client.knowledgebase_client_id) {
          const match = clients.find((c) => c.client_id === client.knowledgebase_client_id);
          if (match) {
            matchedKbId = match.client_id;
            matchedKbName = match.client_name;
          }
        }

        if (!matchedKbId) {
          let best = null;
          let bestScore = 0;
          clients.forEach((c) => {
            const fields = [c.client_name, ...(c.nicknames || [])];
            const score = Math.max(...fields.map(scoreCandidate));
            if (score > bestScore) {
              best = c;
              bestScore = score;
            }
          });
          if (best && bestScore >= 60) {
            matchedKbId = best.client_id;
            matchedKbName = best.client_name;
          }
        }

        if (matchedKbId) {
          setKbClientId(matchedKbId);
          setKbClientName(matchedKbName);
        }

        const candidatesForSp = [matchedKbName, rawName].filter(Boolean);
        let bestSp = null;
        let bestSpScore = 0;

        spFolders.forEach((folder) => {
          const cleaned = folder.replace(/_REBELLIONAIRE/gi, '');
          const score = Math.max(
            scoreCandidate(cleaned),
            ...candidatesForSp.map((cand) => {
              const nf = normalize(cleaned);
              const nc = normalize(cand);
              if (!nf || !nc) return 0;
              if (nf === nc) return 100;
              if (nf.includes(nc) || nc.includes(nf)) return 70;
              return 0;
            }),
          );

          if (score > bestSpScore) {
            bestSp = folder;
            bestSpScore = score;
          }
        });

        if (bestSp && bestSpScore >= 60) {
          setSpClient(bestSp);
        }
      } catch (loadErr) {
        console.error('Failed to load publish data:', loadErr);
      }
    };

    loadData();
  }, [client.knowledgebase_client_id, client.name]);

  useEffect(() => {
    if (spClient && spClient !== '__new__') loadFolders(spClient);
    else setFolderPath('');
  }, [spClient, loadFolders]);

  const handleSpClientChange = (value) => {
    setSpClient(value);
    setNewSpClientName('');

    if (value && value !== '__new__') {
      const normalized = value.replace(/_REBELLIONAIRE/gi, '').toLowerCase().trim();

      let match = kbClients.find((c) => c.client_name.toLowerCase().trim() === normalized);
      if (!match) {
        match = kbClients.find((c) => c.client_name.toLowerCase().includes(normalized) || normalized.includes(c.client_name.toLowerCase()));
      }
      if (!match) {
        match = kbClients.find((c) => c.nicknames?.some((n) => {
          const nick = n.toLowerCase().trim();
          return nick === normalized || nick.includes(normalized) || normalized.includes(nick);
        }));
      }

      setKbClientId(match?.client_id || '');
      setKbClientName(match?.client_name || '');
      setNewKbClientName('');
    } else if (!value) {
      setKbClientId('');
      setKbClientName('');
      setNewKbClientName('');
    }
  };

  const handleKbClientChange = (event) => {
    const selectedId = event.target.value;
    if (selectedId === '__new__') {
      setKbClientId('__new__');
      setKbClientName('');
      const suggestion = client.name.includes(',')
        ? client.name.trim()
        : client.name.trim().split(/\s+/).reverse().join(', ');
      setNewKbClientName(suggestion);
      return;
    }

    const selected = kbClients.find((c) => c.client_id === selectedId);
    setKbClientId(selectedId);
    setKbClientName(selected?.client_name || '');
    setNewKbClientName('');
  };

  const handlePublish = async () => {
    const resolvedSpFolder = spClient === '__new__' ? newSpClientName.trim() : spClient;
    let resolvedKbClientId = kbClientId;
    let resolvedKbClientName = kbClientName;

    if (!resolvedSpFolder) {
      setError('Please select or create a SharePoint folder.');
      return;
    }

    if (kbClientId === '__new__') {
      const name = newKbClientName.trim();
      if (!name) {
        setError('Please enter a name for the new knowledgebase client.');
        return;
      }
      resolvedKbClientName = name;
    } else if (!kbClientId) {
      setError('Please select or create a knowledgebase client.');
      return;
    }

    if (!folderPath && spClient !== '__new__') {
      setError('Please select a SharePoint destination folder.');
      return;
    }

    if (!filename.trim()) {
      setError('Filename cannot be empty.');
      return;
    }

    setLoading(true);
    setError('');

    let resolvedFolderPath = folderPath;

    try {
      if (spClient === '__new__') {
        setStatusMsg('Creating SharePoint client folder...');
        try {
          await sharepointService.createClient(resolvedSpFolder);
        } catch (createErr) {
          console.warn('SharePoint client creation failed (may already exist):', createErr);
        }
        resolvedFolderPath = await loadFolders(resolvedSpFolder);
        if (!resolvedFolderPath) {
          throw new Error('SharePoint folder could not be located. Please verify and retry.');
        }
      }

      if (kbClientId === '__new__') {
        setStatusMsg('Creating knowledgebase client...');
        const kbResult = await clientHelperService.createKbClient({ client_name: resolvedKbClientName });
        resolvedKbClientId = kbResult.client_id;
      }

      if (!resolvedFolderPath) {
        throw new Error('No destination folder available.');
      }

      setStatusMsg('Generating Personal Data Sheet PDF...');
      const detail = await pdsService.getClientDetail(client.client_id);
      const formData = detail?.form?.form_data || {};
      const pdfBlob = buildPdsPdfBlob(formData, {
        clientName: client.name,
        clientEmail: client.email,
      });
      const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });

      setStatusMsg('Uploading to staging...');
      const uploadResult = await sharepointService.uploadFile(
        pdfFile,
        resolvedSpFolder,
        resolvedKbClientId,
        null,
        { skipAnalysis: true, skipRedaction: true },
      );

      const fileId = uploadResult.fileId;
      if (!fileId) {
        throw new Error('Upload failed - no file ID returned.');
      }

      setStatusMsg('Setting filename...');
      await sharepointService.updateFileName(fileId, filename, resolvedSpFolder);

      setStatusMsg('Publishing to SharePoint and knowledgebase...');
      const result = await sharepointService.approveFileAndSummary(
        fileId,
        resolvedSpFolder,
        resolvedKbClientId,
        '',
        resolvedFolderPath,
        overwrite,
        'Mixed',
        'Mixed',
        'NONE',
      );

      if (result.success) {
        onSuccess(result);
      } else if (result.error === 'FILE_EXISTS') {
        setError('A file with this name already exists in the selected folder. Enable overwrite to replace it.');
      } else {
        setError(result.error || result.message || 'Publish failed. Please try again.');
      }
    } catch (publishErr) {
      console.error('Publish error:', publishErr);
      setError(publishErr.response?.data?.detail || publishErr.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
      setStatusMsg('');
    }
  };

  const resolvedSpFolderCheck = spClient === '__new__' ? newSpClientName.trim() : spClient;
  const canSubmit = !loading
    && !!resolvedSpFolderCheck
    && !!(kbClientId === '__new__' ? newKbClientName.trim() : kbClientId)
    && !!filename.trim()
    && !!(spClient === '__new__' || folderPath);
  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-cloud-upload me-2" />
              Upload Personal Data Sheet to SharePoint - <span className="text-muted">{client.name}</span>
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={loading} />
          </div>

          <div className="modal-body">
            {error && (
              <div className="alert alert-danger d-flex align-items-start gap-2">
                <i className="bi bi-exclamation-triangle-fill mt-1 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-semibold" htmlFor="pds-sp-folder">
                SharePoint Folder <span className="text-danger">*</span>
              </label>
              <select id="pds-sp-folder" className="form-select" value={spClient} onChange={(e) => handleSpClientChange(e.target.value)} disabled={loading}>
                <option value="">- Select SharePoint folder -</option>
                <option value="__new__">+ Create new SharePoint folder</option>
                {[...spClients].sort((a, b) => a.localeCompare(b)).map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              {spClient === '__new__' && (
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder='Folder name, e.g. "Smith, Alex"'
                  value={newSpClientName}
                  onChange={(e) => setNewSpClientName(e.target.value)}
                  disabled={loading}
                />
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold" htmlFor="pds-kb-client">
                Knowledgebase Client <span className="text-danger">*</span>
              </label>
              <select id="pds-kb-client" className="form-select" value={kbClientId} onChange={handleKbClientChange} disabled={loading}>
                <option value="">- Select a client -</option>
                <option value="__new__">+ Create new knowledgebase client</option>
                {[...kbClients].sort((a, b) => a.client_name.localeCompare(b.client_name)).map((c) => (
                  <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                ))}
              </select>
              {kbClientId === '__new__' && (
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder='Name, e.g. "Smith, Alex"'
                  value={newKbClientName}
                  onChange={(e) => setNewKbClientName(e.target.value)}
                  disabled={loading}
                />
              )}
            </div>

            {spClient && spClient !== '__new__' && (
              <div className="mb-3">
                <label className="form-label fw-semibold" htmlFor="pds-sp-destination">
                  SharePoint Destination
                </label>
                <input
                  id="pds-sp-destination"
                  type="text"
                  className="form-control"
                  value={folderPath || (loadingFolders ? 'Resolving main folder...' : 'Main folder (auto)')}
                  disabled
                  readOnly
                />
                <div className="form-text">
                  The Personal Data Sheet goes in the client&rsquo;s main folder,
                  not Client Services Docs or Financial Planning.
                </div>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-semibold" htmlFor="pds-filename">
                PDF Filename <span className="text-danger">*</span>
              </label>
              <input
                id="pds-filename"
                type="text"
                className="form-control"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-check mb-1">
              <input
                id="pds-overwrite"
                className="form-check-input"
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                disabled={loading}
              />
              <label htmlFor="pds-overwrite" className="form-check-label">
                Overwrite existing file if it already exists
              </label>
            </div>

            {statusMsg && (
              <div className="mt-3 alert alert-info d-flex align-items-center gap-2 mb-0">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                <span>{statusMsg}</span>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handlePublish} disabled={!canSubmit}>
              {loading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
